import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { LogIn, Phone, Mail } from 'lucide-react';
import { 
  signInWithEmail,
  createAccountWithEmail,
  resetPassword,
  setupRecaptcha, 
  signInWithPhone, 
  verifyPhoneCode,
  auth 
} from '@/lib/firebase';
import { useAuth } from '@/components/AuthProvider';

export default function LoginPage() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [showPhoneAuth, setShowPhoneAuth] = useState(false);
  const [showEmailAuth, setShowEmailAuth] = useState(true);
  const [showVerification, setShowVerification] = useState(false);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [recaptchaVerifier, setRecaptchaVerifier] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setLocation('/');
    }
  }, [user, setLocation]);

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isCreatingAccount) {
        await createAccountWithEmail(email, password);
      } else {
        await signInWithEmail(email, password);
      }
      setLocation('/');
    } catch (error: any) {
      console.error('Email auth error:', error);
      alert(error.message || 'Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      alert('Please enter your email address first.');
      return;
    }
    try {
      await resetPassword(email);
      alert('Password reset email sent! Check your inbox.');
    } catch (error: any) {
      console.error('Password reset error:', error);
      alert(error.message || 'Password reset failed. Please try again.');
    }
  };

  const handlePhoneSignIn = async () => {
    try {
      if (!recaptchaVerifier) {
        const verifier = setupRecaptcha('recaptcha-container');
        setRecaptchaVerifier(verifier);
        const confirmationResult = await signInWithPhone(phoneNumber, verifier);
        setVerificationId(confirmationResult.verificationId);
        setShowVerification(true);
      } else {
        const confirmationResult = await signInWithPhone(phoneNumber, recaptchaVerifier);
        setVerificationId(confirmationResult.verificationId);
        setShowVerification(true);
      }
    } catch (error) {
      console.error('Phone sign in error:', error);
      alert('Phone verification failed. Please try again.');
    }
  };

  const handleVerifyCode = async () => {
    try {
      await verifyPhoneCode(verificationId, verificationCode);
      setLocation('/');
    } catch (error) {
      console.error('Verification error:', error);
      alert('Invalid verification code. Please try again.');
    }
  };

  if (user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <LogIn size={48} className="mx-auto text-red-700 mb-4" />
          <h2 className="text-3xl font-bold text-gray-900">
            Welcome to BlackBook
          </h2>
          <p className="mt-2 text-gray-600">
            Sign in to discover and support Black-owned businesses
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="space-y-4">
            {/* Email/Phone Toggle */}
            <div className="flex rounded-lg bg-gray-100 p-1">
              <button
                onClick={() => { setShowEmailAuth(true); setShowPhoneAuth(false); }}
                className={`flex-1 flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium ${
                  showEmailAuth ? 'bg-white text-red-700 shadow' : 'text-gray-600'
                }`}
              >
                <Mail className="h-4 w-4 mr-2" />
                Email
              </button>
              <button
                onClick={() => { setShowPhoneAuth(true); setShowEmailAuth(false); }}
                className={`flex-1 flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium ${
                  showPhoneAuth ? 'bg-white text-red-700 shadow' : 'text-gray-600'
                }`}
              >
                <Phone className="h-4 w-4 mr-2" />
                Phone
              </button>
            </div>

            {/* Email Authentication Form */}
            {showEmailAuth && (
              <form onSubmit={handleEmailAuth} className="space-y-4">
                <div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email address"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600"
                    required
                  />
                </div>
                <div>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600"
                    required
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-red-700 text-white py-2 px-4 rounded-md hover:bg-red-800 disabled:opacity-50"
                >
                  {loading ? 'Please wait...' : (isCreatingAccount ? 'Create Account' : 'Sign In')}
                </button>
                
                <div className="flex justify-between text-sm">
                  <button
                    type="button"
                    onClick={() => setIsCreatingAccount(!isCreatingAccount)}
                    className="text-red-700 hover:text-green-700"
                  >
                    {isCreatingAccount ? 'Already have an account?' : 'Need an account?'}
                  </button>
                  <button
                    type="button"
                    onClick={handlePasswordReset}
                    className="text-red-700 hover:text-green-700"
                  >
                    Forgot password?
                  </button>
                </div>
              </form>
            )}

            {/* Phone Authentication */}
            {showPhoneAuth && (
              <div className="space-y-4">
                {!showVerification ? (
                  <>
                    <div>
                      <input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        placeholder="+1 (555) 123-4567"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600"
                      />
                    </div>
                    <div id="recaptcha-container"></div>
                    <button
                      onClick={handlePhoneSignIn}
                      className="w-full py-2 px-4 bg-red-700 text-white rounded-md hover:bg-red-800"
                    >
                      Send Verification Code
                    </button>
                  </>
                ) : (
                  <>
                    <div>
                      <input
                        type="text"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        placeholder="Enter verification code"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600"
                      />
                    </div>
                    <button
                      onClick={handleVerifyCode}
                      className="w-full py-2 px-4 bg-red-700 text-white rounded-md hover:bg-red-800"
                    >
                      Verify & Sign In
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}